/**
 * Color utility functions
 * Helper functions for color manipulation and theme-aware color operations
 */

/**
 * Convert hex color to rgba with opacity
 * @param hex - Hex color string (e.g., '#0057B8')
 * @param alpha - Opacity value between 0 and 1
 * @returns rgba color string
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Parse hex color to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace('#', '');
  return {
    r: parseInt(cleanHex.slice(0, 2), 16),
    g: parseInt(cleanHex.slice(2, 4), 16),
    b: parseInt(cleanHex.slice(4, 6), 16),
  };
};

/**
 * Convert RGB to hex
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
};

/**
 * Darken a color by a percentage
 * @param hex - Hex color string
 * @param amount - Percentage to darken (0-100)
 * @returns Darkened hex color string
 */
export const darken = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  const factor = 1 - amount / 100;
  return rgbToHex(
    Math.round(rgb.r * factor),
    Math.round(rgb.g * factor),
    Math.round(rgb.b * factor)
  );
};

/**
 * Lighten a color by a percentage
 * @param hex - Hex color string
 * @param amount - Percentage to lighten (0-100)
 * @returns Lightened hex color string
 */
export const lighten = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  const factor = amount / 100;
  return rgbToHex(
    Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor)),
    Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor)),
    Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor))
  );
};

/**
 * Mix two colors together
 * @param color1 - First hex color
 * @param color2 - Second hex color
 * @param weight - Weight of first color (0-1, default 0.5)
 * @returns Mixed hex color string
 */
export const mix = (color1: string, color2: string, weight: number = 0.5): string => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  return rgbToHex(
    Math.round(rgb1.r * weight + rgb2.r * (1 - weight)),
    Math.round(rgb1.g * weight + rgb2.g * (1 - weight)),
    Math.round(rgb1.b * weight + rgb2.b * (1 - weight))
  );
};

/**
 * Calculate relative luminance for contrast calculation
 */
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Get contrast ratio between two colors
 * @param color1 - First hex color
 * @param color2 - Second hex color
 * @returns Contrast ratio (1-21, higher is better)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Get appropriate contrast color (black or white) for a background
 * @param backgroundColor - Background hex color
 * @param threshold - Contrast ratio threshold (default 4.5 for WCAG AA)
 * @returns '#000000' or '#FFFFFF'
 */
export const getContrastColor = (backgroundColor: string, threshold: number = 4.5): string => {
  const whiteContrast = getContrastRatio(backgroundColor, '#FFFFFF');
  const blackContrast = getContrastRatio(backgroundColor, '#000000');
  
  // If both meet threshold, prefer higher contrast
  if (whiteContrast >= threshold && blackContrast >= threshold) {
    return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
  }
  
  // Return the one that meets threshold, or default to white
  return whiteContrast >= threshold ? '#FFFFFF' : '#000000';
};

/**
 * Generate a gradient from two colors
 * @param color1 - Start color (hex)
 * @param color2 - End color (hex)
 * @param direction - Gradient direction (default '135deg')
 * @param stops - Optional color stops array
 * @returns CSS gradient string
 */
export const generateGradient = (
  color1: string,
  color2: string,
  direction: string = '135deg',
  stops?: Array<{ color: string; position: string }>
): string => {
  if (stops && stops.length > 0) {
    const stopStrings = stops.map(stop => `${stop.color} ${stop.position}`);
    return `linear-gradient(${direction}, ${stopStrings.join(', ')})`;
  }
  return `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;
};

/**
 * Generate a radial gradient
 */
export const generateRadialGradient = (
  color1: string,
  color2: string,
  shape: 'circle' | 'ellipse' = 'circle',
  position: string = 'center'
): string => {
  return `radial-gradient(${shape} at ${position}, ${color1} 0%, ${color2} 100%)`;
};

/**
 * Adjust color opacity
 */
export const withOpacity = (color: string, opacity: number): string => {
  if (color.startsWith('#')) {
    return hexToRgba(color, opacity);
  }
  // If already rgba/rgb, try to extract and modify
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
  }
  return color;
};
